import { RpcError } from '@protobuf-ts/runtime-rpc'
import { ClosableProps } from 'components/no-ui/context/modal-provider'
import { BaseActionForm } from 'components/pages/task/action/form/BaseActionForm'
import { getCertifyPresets } from 'components/pages/task/action/messages'
import { useAlert } from 'hooks/useAlert/index'
import { TaskValue } from 'lib/actions/task'
import { ReviewPendingDecision } from 'pbts/c1/api/accessreview/v1/accessreview_pending'
import { FC, useCallback } from 'react'
import { useIntl } from 'react-intl'
import { usePendingTicketActionTracker } from 'redux_toolkit/lib/PendingTicketActionTracker'

type ApproveWithCommentPendingActionDialogProps = {
    task: TaskValue
} & ClosableProps

export const ApproveWithCommentPendingActionDialog: FC<ApproveWithCommentPendingActionDialogProps> = ({
    task,
    close,
}) => {
    const intl = useIntl()
    const toast = useAlert()
    const pendingTicketActionTracker = usePendingTicketActionTracker()

    const handleSave = useCallback(async (comment: string): Promise<void> => {
        if (!pendingTicketActionTracker) return

        try {
            await pendingTicketActionTracker.mutatePendingAction(task, ReviewPendingDecision.APPROVE, comment)
        } catch (err: unknown) {
            toast(
                intl.formatMessage(
                    {
                        id: 'pending_action_approve_with_comment_error',
                        defaultMessage: 'Error queuing approval {code}: {msg}',
                    },
                    {
                        code: (err as RpcError).code,
                        msg: (err as RpcError).message,
                    },
                ),
                'error',
            )
            return
        }

        toast(
            intl.formatMessage({
                id: 'pending_action_approve_queued',
                defaultMessage: 'Approval queued',
            }),
            'success',
        )
        close()
    }, [pendingTicketActionTracker, task, intl, toast, close])

    return <BaseActionForm
        closeDialog={close}
        open
        onApprove={handleSave}
        title={intl.formatMessage({
            id: 'component_task_action_form_certify_pending_title',
            defaultMessage: 'Certify access with comment',
        })}
        description={intl.formatMessage({
            id: 'component_action_form_certify_pending_description',
            defaultMessage: 'Add your comment and queue this review for submission.',
        })}
        presets={getCertifyPresets(intl)}
        actionText={intl.formatMessage({
            id: 'pending_action_queue_approval',
            defaultMessage: 'Certify',
        })}
    />
}
